import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
  Slide,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AuthContext from "../../contexts/AuthContext";
import Auth from "../Auth/Auth";
import WalkUpWelcome from "./components/WalkUpWelcome";
import WalkUpMessage from "./components/WalkUpMessage";
import request from "../../utilities/requests";
import { toast } from "../../utilities/SnackBarUtils";
import {
  setWalkUpContext,
  getWalkUpContext,
  setWalkUpFlowStarted,
  getWalkUpFlowStarted,
  clearWalkUpFlowStarted,
  isWalkupOnboarded,
  setWalkupOnboarded,
} from "../../utilities/walkup";
import { setSetupContext } from "../../utilities/setupContext";
import walkUpSource from "../Setup/sources/walkup";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CenteredSpinner = ({ label }) => (
  <Grid container direction="column" alignItems="center" justifyContent="center" spacing={2} sx={{ minHeight: "60vh" }}>
    <Grid item>
      <CircularProgress />
    </Grid>
    {label && (
      <Grid item>
        <Typography variant="subtitle1">{label}</Typography>
      </Grid>
    )}
  </Grid>
);

const WalkUp = ({ config, orgId }) => {
  const { eventId, token } = useParams();
  const { signedIn } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = React.useState(true);
  const [eventInfo, setEventInfo] = React.useState(null);
  const [count, setCount] = React.useState(null);
  const [reason, setReason] = React.useState(null);
  const [alreadyRegistered, setAlreadyRegistered] = React.useState(false);
  // Returning scanners who already completed onboarding skip the welcome overlay
  // and the create-account default.
  const onboarded = isWalkupOnboarded();
  const [welcomeOpen, setWelcomeOpen] = React.useState(!onboarded);
  const [registering, setRegistering] = React.useState(false);
  const autoAttempted = React.useRef(false);

  // Resolve context from the URL params, falling back to stored context (the
  // /confirm magic-link route has no params).
  const ctx = React.useMemo(() => {
    if (eventId && token) return { eventId, token };
    return getWalkUpContext();
  }, [eventId, token]);

  // This tab/device is mid walk-up flow if the flag matches the event.
  const inFlow = !!ctx?.eventId && getWalkUpFlowStarted() === String(ctx.eventId);

  React.useEffect(() => {
    if (eventId && token) setWalkUpContext(eventId, token);
  }, [eventId, token]);

  const completeRegistration = async () => {
    if (!ctx?.eventId || !ctx?.token) return;
    setRegistering(true);
    const result = await request("events/selfregister", "post", {
      eventId: ctx.eventId,
      token: ctx.token,
    });
    clearWalkUpFlowStarted();

    if (!result?.success) {
      setRegistering(false);
      toast.error(result?.error || t("walkup.error.generic"));
      navigate("/");
      return;
    }

    setWalkupOnboarded();
    const descriptor = walkUpSource.buildDescriptor({
      eventId: ctx.eventId,
      token: ctx.token,
      response: result,
    });
    setSetupContext(descriptor);
    navigate(`/setup/${ctx.eventId}`, { state: { setup: descriptor } });
  };

  // Public landing info (display + live count + validity). Skipped for the
  // signed-in in-flow case, which goes straight to registration.
  React.useEffect(() => {
    let active = true;
    const run = async () => {
      if (signedIn && inFlow) {
        setLoading(false);
        return;
      }
      if (!ctx?.eventId || !ctx?.token) {
        setReason("notFound");
        setLoading(false);
        return;
      }
      try {
        const url = `${process.env.REACT_APP_TNFT_SERVER}/unauth/walkup/${ctx.eventId}/${ctx.token}`;
        const res = await axios(url);
        const data = res?.data;
        if (!active) return;
        if (data?.valid) {
          setEventInfo(data.data);
          setCount(data.data?.checkedInCount ?? null);
          setReason(null);
        } else {
          setEventInfo(data?.data || null);
          setReason(data?.reason || "notFound");
        }
      } catch (e) {
        if (active) setReason("notFound");
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [ctx, signedIn, inFlow]);

  // Mark the flow as started once an unauthenticated user reaches a valid
  // welcome, so completing signup/login auto-registers them afterwards.
  React.useEffect(() => {
    if (!signedIn && !reason && eventInfo && ctx?.eventId) {
      setWalkUpFlowStarted(ctx.eventId);
    }
  }, [signedIn, reason, eventInfo, ctx]);

  // Signed-in handling: in-flow users auto-register; fresh arrivals get the
  // status check so we can show "Welcome back" vs "You're already in".
  React.useEffect(() => {
    // Set the guard synchronously (before the async work) so a re-render or
    // StrictMode double-invoke can't fire a second self-registration.
    if (!signedIn || autoAttempted.current) return;
    autoAttempted.current = true;
    const run = async () => {
      if (!ctx?.eventId || !ctx?.token) {
        navigate("/");
        return;
      }
      if (inFlow) {
        await completeRegistration();
        return;
      }
      try {
        const result = await request(
          `events/walkup/status?eventId=${ctx.eventId}&token=${ctx.token}`,
        );
        if (result?.valid) {
          setAlreadyRegistered(!!result.alreadyRegistered);
        } else if (result?.reason) {
          setReason(result.reason);
        }
      } catch (e) {
        /* leave as-is; public fetch reason still applies */
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn, inFlow, ctx]);

  const handleRegisterCta = async () => {
    await completeRegistration();
  };

  // Signed-in, in-flow: registration is happening; just show progress.
  if (signedIn && inFlow) {
    return <CenteredSpinner label={t("walkup.adding")} />;
  }

  if (loading) {
    return <CenteredSpinner />;
  }

  if (reason) {
    return <WalkUpMessage reason={reason} event={eventInfo} />;
  }

  // Signed-in fresh arrival: welcome-back / already-in with a register CTA.
  if (signedIn) {
    return (
      <WalkUpWelcome
        variant={alreadyRegistered ? "already" : "returning"}
        event={eventInfo}
        count={count}
        busy={registering}
        onPrimary={handleRegisterCta}
      />
    );
  }

  // Unauthenticated + valid: the existing auth form, with the welcome overlay
  // on top. Dismissing the overlay reveals the form beneath.
  return (
    <Box>
      <Auth
        config={config}
        orgId={orgId}
        initialPage={onboarded ? "login" : "signup"}
      />
      <Dialog
        fullScreen
        open={welcomeOpen}
        onClose={() => setWelcomeOpen(false)}
        TransitionComponent={Transition}
      >
        <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", minHeight: "100%", background: "#fff" }}>
          <WalkUpWelcome
            variant="new"
            event={eventInfo}
            count={count}
            onPrimary={() => setWelcomeOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default WalkUp;
